import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  isDashboardMock,
  mockResponse,
  MOCK_SEGMENTS,
} from '../dashboard-shopify.mock';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import {
  BehavioralIntelligenceSegment,
  BehavioralIntelligenceSegmentsResponse,
  InsightsSignalsService,
} from 'src/app/signals-insights/signals-insights.service';
import { signalsTarget } from 'src/app/signals-insights/signals-target';
import { segmentDisplayName } from 'src/app/signals-insights/signals-experiments/signals-experiments.service';
import {
  FAMILY_FALLBACK_TINTS,
  FAMILY_STYLES,
} from 'src/app/signals-insights/signals-flow/signals-flow.data';

/** On-site actions; a segment counts as live only when an off-site channel is linked. */
const ONSITE_CHANNELS = [
  'suppress',
  'personalize',
  'assist',
  'nudge',
  'incentive',
];

const MAX_SEGMENTS = 5;

interface SignalsSegmentRow {
  name: string;
  share: number;
  sessions: number;
  live: boolean;
}

interface SignalsStage {
  name: string;
  tint: string;
  sessions: number;
  liveCount: number;
  segmentCount: number;
  segments: SignalsSegmentRow[];
}

/**
 * Compact Signals card for the Shopify LIVE dashboard.
 *
 * A summary of the Signals > Overview board (signals-insights/signals-flow),
 * from the same `/behavioral-intelligence/segments` response: the stage the
 * board's header reports on (the first one with sub-segments) and its top
 * segments by share of sessions. Stages partition the same traffic in
 * different ways, so their figures are never summed.
 */
@Component({
  selector: 'app-shopify-signals-table',
  templateUrl: './shopify-signals-table.component.html',
  styleUrls: ['./shopify-signals-table.component.css'],
})
export class ShopifySignalsTableComponent implements OnInit, OnDestroy {
  private signalsService = inject(InsightsSignalsService);
  private targetService = inject(CoreTargetService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);

  private request?: Subscription;
  // `?dashboardMock=1`: dummy responses instead of the API (see the mock file).
  private readonly mock = isDashboardMock();

  public stage: SignalsStage | null = null;
  public isLoading = true;
  public hasError = false;

  ngOnInit(): void {
    this.loadSegments();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  public loadSegments(): void {
    const target = signalsTarget(
      this.targetService.getSelectedTarget('target'),
    );

    this.request?.unsubscribe();
    this.isLoading = true;
    this.hasError = false;

    if (!target) {
      this.stage = null;
      this.isLoading = false;
      return;
    }

    const segments$ = this.mock
      ? mockResponse(MOCK_SEGMENTS)
      : this.signalsService.getBehavioralIntelligenceSegments({
          target,
          startDate: String(this.localStorageService.get('startDate') || ''),
          endDate: String(this.localStorageService.get('endDate') || ''),
        });

    this.request = segments$.subscribe({
        next: (response) => {
          this.stage = this.toStage(Array.isArray(response) ? response : []);
          this.isLoading = false;
        },
        error: () => {
          this.stage = null;
          this.hasError = true;
          this.isLoading = false;
        },
      });
  }

  // Opens the full Signals overview (the flow board).
  public view(): void {
    this.router.navigate(['/signals']);
  }

  private toStage(
    response: BehavioralIntelligenceSegmentsResponse[],
  ): SignalsStage | null {
    const index = response.findIndex((stage) => stage?.items?.length);
    if (index < 0) return null;

    const stage = response[index];
    const id = stage.value || stage.selectedStage || stage.label;
    const segments = stage.items.map((item) => this.toRow(item));

    return {
      name: segmentDisplayName(stage.label || id),
      tint:
        FAMILY_STYLES[id]?.tint ??
        FAMILY_FALLBACK_TINTS[index % FAMILY_FALLBACK_TINTS.length],
      sessions: segments.reduce((sum, s) => sum + s.sessions, 0),
      liveCount: segments.filter((s) => s.live).length,
      segmentCount: segments.length,
      segments: segments
        .sort((a, b) => b.share - a.share)
        .slice(0, MAX_SEGMENTS),
    };
  }

  private toRow(item: BehavioralIntelligenceSegment): SignalsSegmentRow {
    return {
      name: segmentDisplayName(item.label || item.value),
      share: Math.min(100, this.parseNumber(item.percentage)),
      sessions: this.parseNumber(item.sessions),
      live: (item.channels ?? []).some(
        (c) => !!c.value && !ONSITE_CHANNELS.includes(c.value),
      ),
    };
  }

  // Same parsing as SignalsFlowComponent: missing or invalid figures read as 0.
  private parseNumber(value: number | string | null | undefined): number {
    if (value === null || value === undefined) return 0;
    const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }
}
